import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, Building2, Video, Calendar as CalIcon, Loader2, GraduationCap, Award, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import AppShell from "@/components/layout/AppShell";

function groupSlotsByDate(slots) {
  const map = {};
  for (const s of slots) {
    if (s.status !== "available") continue;
    map[s.date] = map[s.date] || [];
    map[s.date].push(s);
  }
  return Object.entries(map).sort(([a], [b]) => (a > b ? 1 : -1)).slice(0, 14);
}

function fmtDay(iso) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

export default function DoctorDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [doctor, setDoctor] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [ctype, setCtype] = useState("in_person");
  const [showConfirm, setShowConfirm] = useState(false);
  const [booking, setBooking] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [d, s] = await Promise.all([
          api.get(`/doctors/${id}`),
          api.get(`/doctors/${id}/slots`),
        ]);
        setDoctor(d.data);
        setSlots(s.data);
        setCtype(d.data.consultation_types?.[0] || "in_person");
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const grouped = useMemo(() => groupSlotsByDate(slots), [slots]);
  const [activeDate, setActiveDate] = useState(null);
  useEffect(() => { if (grouped.length && !activeDate) setActiveDate(grouped[0][0]); }, [grouped, activeDate]);

  const daySlots = grouped.find(([d]) => d === activeDate)?.[1] || [];

  const book = async () => {
    if (!user) { nav("/login", { state: { from: `/doctors/${id}` } }); return; }
    if (user.role !== "patient") { toast.error("Only patients can book"); return; }
    setBooking(true);
    try {
      const { data } = await api.post("/appointments", {
        doctor_id: id, slot_id: selectedSlot.id, consultation_type: ctype,
      });
      toast.success("Appointment booked!");
      setShowConfirm(false);
      nav(`/patient/appointments`);
    } catch (e) {
      toast.error(formatApiError(e, "Booking failed"));
    } finally { setBooking(false); }
  };

  const content = loading ? (
    <div className="py-20 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
  ) : !doctor ? (
    <div className="py-20 text-center text-slate-500">Doctor not found</div>
  ) : (
    <div>
      <button onClick={() => nav(-1)} className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-4" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left: profile */}
        <div className="lg:col-span-2 space-y-6">
          <div className="doccare-card p-6">
            <div className="flex flex-col sm:flex-row gap-5">
              <img src={doctor.photo} alt={doctor.name} className="w-24 h-24 rounded-2xl object-cover bg-slate-100" />
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl font-bold text-slate-900">{doctor.name}</h1>
                <p className="text-emerald-700 font-semibold">{doctor.specialty}</p>
                <p className="text-sm text-slate-500 mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> {doctor.location}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Badge variant="secondary" className="rounded-full">{doctor.experience_years}+ years</Badge>
                  {doctor.consultation_types?.includes("video") && <Badge className="rounded-full bg-blue-50 text-blue-700 hover:bg-blue-50"><Video className="w-3 h-3 mr-1" /> Video</Badge>}
                  {doctor.consultation_types?.includes("in_person") && <Badge className="rounded-full bg-emerald-50 text-emerald-700 hover:bg-emerald-50"><Building2 className="w-3 h-3 mr-1" /> In-person</Badge>}
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-500">Consultation</p>
                <p className="text-2xl font-bold text-slate-900">${doctor.fee}</p>
              </div>
            </div>
          </div>

          <div className="doccare-card p-6">
            <h3 className="font-semibold text-slate-900 mb-2">About</h3>
            <p className="text-slate-600 text-sm leading-relaxed">{doctor.about}</p>
            <div className="mt-5 grid sm:grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold flex items-center gap-1"><GraduationCap className="w-3.5 h-3.5" /> Qualifications</p>
                <p className="text-sm text-slate-900 mt-1">{doctor.qualifications}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold flex items-center gap-1"><Building2 className="w-3.5 h-3.5" /> Clinic</p>
                <p className="text-sm text-slate-900 mt-1">{doctor.clinic}</p>
              </div>
            </div>
            {doctor.expertise?.length ? (
              <div className="mt-5">
                <p className="text-xs text-slate-500 uppercase font-semibold flex items-center gap-1"><Award className="w-3.5 h-3.5" /> Expertise</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {doctor.expertise.map((e) => <Badge key={e} variant="outline" className="rounded-full">{e}</Badge>)}
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {/* Right: booking */}
        <div className="space-y-4">
          <div className="doccare-card p-5 sticky top-4">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2"><CalIcon className="w-4 h-4" /> Book appointment</h3>
            {grouped.length === 0 ? (
              <p className="mt-4 text-sm text-slate-500">No availability in the next 14 days.</p>
            ) : (
              <>
                <div className="mt-4 flex gap-2 overflow-x-auto no-scrollbar pb-2">
                  {grouped.map(([d]) => (
                    <button key={d} onClick={() => { setActiveDate(d); setSelectedSlot(null); }} className={`px-3 py-2 rounded-xl text-xs font-medium shrink-0 border transition-colors ${activeDate === d ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"}`} data-testid={`date-${d}`}>
                      {fmtDay(d)}
                    </button>
                  ))}
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  {daySlots.map((s) => (
                    <button key={s.id} onClick={() => setSelectedSlot(s)} className={`px-2 py-2 rounded-lg text-xs font-semibold border transition-colors ${selectedSlot?.id === s.id ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-700 border-slate-200 hover:border-emerald-300"}`} data-testid={`slot-${s.id}`}>
                      {s.start_time}
                    </button>
                  ))}
                </div>

                {doctor.consultation_types?.length > 1 && (
                  <div className="mt-4">
                    <label className="text-xs font-semibold text-slate-500">Consultation type</label>
                    <Select value={ctype} onValueChange={setCtype}>
                      <SelectTrigger className="mt-1.5 rounded-xl" data-testid="select-ctype"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {doctor.consultation_types.includes("in_person") && <SelectItem value="in_person">In-person</SelectItem>}
                        {doctor.consultation_types.includes("video") && <SelectItem value="video">Video</SelectItem>}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <Button
                  disabled={!selectedSlot}
                  onClick={() => (user ? setShowConfirm(true) : nav("/login", { state: { from: `/doctors/${id}` } }))}
                  className="w-full mt-4 rounded-xl h-11"
                  data-testid="btn-book"
                >
                  {selectedSlot ? `Book ${fmtDay(selectedSlot.date)} · ${selectedSlot.start_time}` : "Select a slot"}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent data-testid="confirm-modal">
          <DialogHeader><DialogTitle>Confirm appointment</DialogTitle></DialogHeader>
          {selectedSlot && (
            <div className="space-y-2 text-sm">
              <p><span className="text-slate-500">Doctor:</span> <span className="font-semibold">{doctor.name}</span></p>
              <p><span className="text-slate-500">Specialty:</span> {doctor.specialty}</p>
              <p><span className="text-slate-500">Date:</span> {fmtDay(selectedSlot.date)}</p>
              <p><span className="text-slate-500">Time:</span> {selectedSlot.start_time} – {selectedSlot.end_time}</p>
              <p><span className="text-slate-500">Type:</span> {ctype === "video" ? "Video" : "In-person"}</p>
              <p><span className="text-slate-500">Fee:</span> ${doctor.fee}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={book} disabled={booking} className="rounded-xl" data-testid="confirm-book">
              {booking ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm booking"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  if (user && user.role === "patient") return <AppShell>{content}</AppShell>;
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-slate-900 text-white grid place-items-center font-bold">D</div>
            <span className="text-xl font-bold text-slate-900">DocCare</span>
          </Link>
          <div className="flex gap-2">
            <Link to="/doctors"><Button variant="ghost" className="rounded-full">Browse</Button></Link>
            <Link to="/login"><Button className="rounded-full">Sign in</Button></Link>
          </div>
        </div>
      </header>
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-8">{content}</div>
    </div>
  );
}
